import { useEffect, useState, useCallback, useRef } from 'react';
import { api } from '../services/api';
import { ServiceCategoryResponse, CreateServiceCategoryRequest } from '../types';
import { useAuth } from '../hooks/useAuth';

const IMAGE_ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const IMAGE_MAX_SIZE = 5 * 1024 * 1024; // 5 MB — matches StorageService CATEGORY_IMAGE limit

interface EditFormData {
  name: string;
  nameAr: string;
  description: string;
  descriptionAr: string;
  defaultDurationMinutes: number;
  defaultWeight: number;
  defaultCommissionRate: number;
  sortOrder: number;
}

const EMPTY_FORM: EditFormData = {
  name: '',
  nameAr: '',
  description: '',
  descriptionAr: '',
  defaultDurationMinutes: 30,
  defaultWeight: 1,
  defaultCommissionRate: 0,
  sortOrder: 0,
};

function matchesSearch(node: ServiceCategoryResponse, term: string): boolean {
  if (node.name.toLowerCase().includes(term)) return true;
  return node.children.some((c) => matchesSearch(c, term));
}

function filterTree(nodes: ServiceCategoryResponse[], term: string): ServiceCategoryResponse[] {
  return nodes
    .filter((n) => !term || matchesSearch(n, term))
    .map((n) => ({ ...n, children: filterTree(n.children, term) }))
    .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
}

export default function CategoriesPage() {
  const auth = useAuth();
  const [categories, setCategories] = useState<ServiceCategoryResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchText, setSearchText] = useState('');

  // Expand/collapse state
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  // Edit / Add state
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingParentId, setEditingParentId] = useState<string | null>(null);
  const [addingParentId, setAddingParentId] = useState<string | null | undefined>(undefined); // undefined = not adding, null = top-level, string = child
  const [formData, setFormData] = useState<EditFormData>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  // Image upload state
  const [imageBusyId, setImageBusyId] = useState<string | null>(null);
  const pendingImageCategoryId = useRef<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const fetchCategories = useCallback(() => {
    if (!auth.token) return;
    setLoading(true);
    setError(null);
    api.listCategories(false, auth.token)
      .then(setCategories)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [auth.token]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const toggleExpand = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const startEdit = (node: ServiceCategoryResponse) => {
    setEditingId(node.id);
    setEditingParentId(node.parentId);
    setAddingParentId(undefined);
    setFormData({
      name: node.name,
      nameAr: node.nameAr || '',
      description: node.description || '',
      descriptionAr: node.descriptionAr || '',
      defaultDurationMinutes: node.defaultDurationMinutes,
      defaultWeight: node.defaultWeight,
      defaultCommissionRate: (node.defaultCommissionRate || 0) * 100,
      sortOrder: node.sortOrder,
    });
  };

  const startAdd = (parentId: string | null) => {
    setAddingParentId(parentId);
    setEditingId(null);
    setFormData(EMPTY_FORM);
    if (parentId) {
      setExpanded((prev) => new Set(prev).add(parentId));
    }
  };

  const cancelForm = () => {
    setEditingId(null);
    setEditingParentId(null);
    setAddingParentId(undefined);
    setFormData(EMPTY_FORM);
  };

  const handleSave = async () => {
    if (!auth.token || !formData.name.trim()) return;
    setSaving(true);
    setError(null);
    try {
      if (editingId) {
        const data: CreateServiceCategoryRequest = {
          name: formData.name.trim(),
          // Always sent, never undefined: the backend keeps the stored Arabic name when the
          // field is omitted, so an edit that dropped it would leave a stale value behind.
          nameAr: formData.nameAr.trim(),
          description: formData.description.trim() || undefined,
          descriptionAr: formData.descriptionAr.trim(),
          // Preserve the existing parent — backend's PUT clears parent when null is sent
          parentId: editingParentId || undefined,
          defaultDurationMinutes: formData.defaultDurationMinutes,
          defaultWeight: formData.defaultWeight,
          defaultCommissionRate: formData.defaultCommissionRate / 100,
          sortOrder: formData.sortOrder,
        };
        await api.updateCategory(editingId, data, auth.token);
      } else if (addingParentId !== undefined) {
        const data: CreateServiceCategoryRequest = {
          name: formData.name.trim(),
          // Always sent, never undefined: the backend keeps the stored Arabic name when the
          // field is omitted, so an edit that dropped it would leave a stale value behind.
          nameAr: formData.nameAr.trim(),
          description: formData.description.trim() || undefined,
          descriptionAr: formData.descriptionAr.trim(),
          parentId: addingParentId || undefined,
          defaultDurationMinutes: formData.defaultDurationMinutes,
          defaultWeight: formData.defaultWeight,
          defaultCommissionRate: formData.defaultCommissionRate / 100,
          sortOrder: formData.sortOrder,
        };
        await api.createCategory(data, auth.token);
      }
      cancelForm();
      fetchCategories();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async (id: string) => {
    if (!auth.token) return;
    if (!window.confirm('This will also deactivate all children. Continue?')) return;
    setError(null);
    try {
      await api.deactivateCategory(id, auth.token);
      fetchCategories();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to deactivate');
    }
  };

  const triggerImagePicker = (categoryId: string) => {
    pendingImageCategoryId.current = categoryId;
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
  };

  const handleImageFileSelected = async (file: File) => {
    const categoryId = pendingImageCategoryId.current;
    pendingImageCategoryId.current = null;
    if (!auth.token || !categoryId) return;

    if (!IMAGE_ACCEPTED_TYPES.includes(file.type)) {
      setError('Image must be JPEG, PNG, or WebP');
      return;
    }
    if (file.size > IMAGE_MAX_SIZE) {
      setError('Image must be 5 MB or smaller');
      return;
    }

    setError(null);
    setImageBusyId(categoryId);
    try {
      const { uploadUrl, storageKey } = await api.getCategoryUploadUrl(
        categoryId,
        {
          fileName: file.name,
          contentType: file.type,
          mediaType: 'CATEGORY_IMAGE',
          fileSize: file.size,
        },
        auth.token,
      );

      const putResponse = await fetch(uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': file.type },
        body: file,
      });
      if (!putResponse.ok) {
        throw new Error(`S3 upload failed (${putResponse.status})`);
      }

      await api.confirmCategoryUpload(
        categoryId,
        {
          storageKey,
          mediaType: 'CATEGORY_IMAGE',
          originalFilename: file.name,
          contentType: file.type,
          fileSize: file.size,
        },
        auth.token,
      );

      fetchCategories();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to upload image');
    } finally {
      setImageBusyId(null);
    }
  };

  const handleImageRemove = async (categoryId: string) => {
    if (!auth.token) return;
    if (!window.confirm('Remove the category image?')) return;
    setError(null);
    setImageBusyId(categoryId);
    try {
      await api.deleteCategoryImage(categoryId, auth.token);
      fetchCategories();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove image');
    } finally {
      setImageBusyId(null);
    }
  };

  const handleReactivate = async (id: string) => {
    if (!auth.token) return;
    setError(null);
    try {
      await api.reactivateCategory(id, auth.token);
      fetchCategories();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reactivate');
    }
  };

  const renderForm = () => (
    <div style={{ padding: '0.75rem', background: '#f8fafc', border: '1px solid var(--admin-border)', borderRadius: '6px', marginTop: '0.5rem', marginBottom: '0.5rem' }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
        <div>
          <label style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--admin-text-muted)', display: 'block', marginBottom: '0.25rem' }}>Name *</label>
          <input
            className="admin-search-input"
            style={{ width: '100%', minWidth: 0 }}
            value={formData.name}
            onChange={(e) => setFormData((f) => ({ ...f, name: e.target.value }))}
            placeholder="Category name"
          />
        </div>
        <div>
          <label style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--admin-text-muted)', display: 'block', marginBottom: '0.25rem' }}>Name (Arabic)</label>
          <input
            className="admin-search-input"
            style={{ width: '100%', minWidth: 0 }}
            dir="rtl"
            lang="ar"
            value={formData.nameAr}
            onChange={(e) => setFormData((f) => ({ ...f, nameAr: e.target.value }))}
            placeholder="الاسم بالعربية"
          />
        </div>
        <div>
          <label style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--admin-text-muted)', display: 'block', marginBottom: '0.25rem' }}>Description</label>
          <input
            className="admin-search-input"
            style={{ width: '100%', minWidth: 0 }}
            value={formData.description}
            onChange={(e) => setFormData((f) => ({ ...f, description: e.target.value }))}
            placeholder="Optional description"
          />
        </div>
        <div>
          <label style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--admin-text-muted)', display: 'block', marginBottom: '0.25rem' }}>Description (Arabic)</label>
          <input
            className="admin-search-input"
            style={{ width: '100%', minWidth: 0 }}
            dir="rtl"
            lang="ar"
            value={formData.descriptionAr}
            onChange={(e) => setFormData((f) => ({ ...f, descriptionAr: e.target.value }))}
            placeholder="الوصف بالعربية"
          />
        </div>
      </div>
      <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem' }}>
        <button className="admin-btn admin-btn-primary" disabled={saving || !formData.name.trim()} onClick={handleSave}>
          {saving ? 'Saving...' : editingId ? 'Update' : 'Create'}
        </button>
        <button className="admin-btn admin-btn-outline" onClick={cancelForm}>Cancel</button>
      </div>
    </div>
  );

  const renderNode = (node: ServiceCategoryResponse, depth: number) => {
    const isExpanded = expanded.has(node.id);
    const hasChildren = node.children.length > 0;
    const isEditing = editingId === node.id;
    const isAddingChild = addingParentId === node.id;
    const isInactive = !node.active;

    return (
      <div key={node.id} style={{ marginLeft: depth > 0 ? '1.5rem' : 0 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            padding: '0.5rem 0.75rem',
            borderBottom: '1px solid var(--admin-border)',
            opacity: isInactive ? 0.55 : 1,
          }}
        >
          {/* Expand toggle */}
          <button
            onClick={() => toggleExpand(node.id)}
            style={{
              background: 'none',
              border: 'none',
              cursor: hasChildren ? 'pointer' : 'default',
              padding: '0.125rem',
              fontSize: '0.75rem',
              color: 'var(--admin-text-muted)',
              width: '1.25rem',
              visibility: hasChildren ? 'visible' : 'hidden',
            }}
          >
            {isExpanded ? '▼' : '▶'}
          </button>

          {/* Thumbnail */}
          <div
            style={{
              width: '2.25rem',
              height: '2.25rem',
              borderRadius: '4px',
              border: '1px solid var(--admin-border)',
              background: '#f1f5f9',
              flexShrink: 0,
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '0.6rem',
              color: 'var(--admin-text-muted)',
            }}
            title={node.imageKey ?? 'No image'}
          >
            {node.imageDownloadUrl ? (
              <img
                src={node.imageDownloadUrl}
                alt={`${node.name} icon`}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            ) : (
              '—'
            )}
          </div>

          {/* Name and info */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <span style={{ fontWeight: 500, fontSize: '0.875rem', color: 'var(--admin-text)' }}>
              {node.name}
            </span>
            {/* The Arabic name is what customers see in the app, so show it next to the English
                one — a wrong or missing pairing is only spottable side by side (#812). */}
            {node.nameAr ? (
              <span dir="rtl" lang="ar" style={{ marginLeft: '0.5rem', fontSize: '0.8125rem', color: 'var(--admin-text)' }}>
                {node.nameAr}
              </span>
            ) : (
              <span style={{ marginLeft: '0.5rem', fontSize: '0.75rem', color: '#b45309' }}>
                no Arabic name
              </span>
            )}
            {node.description && (
              <span style={{ marginLeft: '0.5rem', fontSize: '0.75rem', color: 'var(--admin-text-muted)' }}>
                {node.description.length > 60 ? node.description.slice(0, 60) + '...' : node.description}
              </span>
            )}
          </div>

          {/* Status badge */}
          {isInactive ? (
            <span className="admin-badge rejected">Inactive</span>
          ) : (
            <span className="admin-badge approved">Active</span>
          )}

          {/* Action buttons */}
          <div style={{ display: 'flex', gap: '0.25rem' }}>
            <button
              className="admin-btn admin-btn-outline admin-btn-sm"
              disabled={imageBusyId === node.id}
              onClick={() => triggerImagePicker(node.id)}
            >
              {imageBusyId === node.id
                ? 'Uploading...'
                : node.imageDownloadUrl
                ? 'Replace Image'
                : 'Upload Image'}
            </button>
            {node.imageDownloadUrl && (
              <button
                className="admin-btn admin-btn-outline admin-btn-sm"
                disabled={imageBusyId === node.id}
                onClick={() => handleImageRemove(node.id)}
              >
                Remove Image
              </button>
            )}
            <button className="admin-btn admin-btn-outline admin-btn-sm" onClick={() => startEdit(node)}>
              Edit
            </button>
            {depth === 0 && (
              <button className="admin-btn admin-btn-outline admin-btn-sm" onClick={() => startAdd(node.id)}>
                Add Subcategory
              </button>
            )}
            {node.active ? (
              <button className="admin-btn admin-btn-danger admin-btn-sm" onClick={() => handleDeactivate(node.id)}>
                Deactivate
              </button>
            ) : (
              <button className="admin-btn admin-btn-success admin-btn-sm" onClick={() => handleReactivate(node.id)}>
                Reactivate
              </button>
            )}
          </div>
        </div>

        {/* Inline edit form */}
        {isEditing && renderForm()}

        {/* Add child form */}
        {isAddingChild && renderForm()}

        {/* Children */}
        {isExpanded && node.children.map((child) => renderNode(child, depth + 1))}
      </div>
    );
  };

  const filteredCategories = filterTree(categories, searchText.toLowerCase().trim());

  return (
    <div>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        style={{ display: 'none' }}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) handleImageFileSelected(f);
        }}
      />
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.25rem' }}>
        <h2 style={{ fontSize: '1.25rem', fontWeight: 600, color: 'var(--admin-text)' }}>
          Service Catalog
        </h2>
        <button className="admin-btn admin-btn-primary" onClick={() => startAdd(null)}>
          Add Category
        </button>
      </div>

      {error && <div className="admin-info-banner danger" style={{ marginBottom: '1rem' }}>{error}</div>}

      <div className="admin-card">
        <div className="admin-toolbar">
          <input
            type="text"
            className="admin-search-input"
            placeholder="Search categories by name..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
        </div>

        {/* Top-level add form */}
        {addingParentId === null && renderForm()}

        {loading ? (
          <div className="admin-loading">
            <div className="admin-loading-spinner"></div>
          </div>
        ) : filteredCategories.length === 0 ? (
          <div className="admin-empty-state">
            <p>{searchText ? 'No categories match your search.' : 'No categories found. Add a category to get started.'}</p>
          </div>
        ) : (
          <div>
            {filteredCategories.map((node) => renderNode(node, 0))}
          </div>
        )}
      </div>
    </div>
  );
}
